import '@xero/xlabs-supportinginfopanel/index.css';

import XUIButton from '@xero/xui/react/button';
import XUIIcon from '@xero/xui/react/icon';
import XUIModal, { XUIModalBody, XUIModalFooter, XUIModalHeader } from '@xero/xui/react/modal';
import { XUIRadio, XUIRadioGroup } from '@xero/xui/react/radio';
import { FC, useEffect, useState } from 'react';
import invalidIcon from '@xero/xui-icon/icons/invalid';
import infoIcon from '@xero/xui-icon/icons/info';
import XLabsSupportingInfoPanel from '@xero/xlabs-supportinginfopanel';
import lang from '../../static/lang/en-NZ';
import { useSelector } from 'react-redux';
import { installationSelectors } from '../../redux/slices/InstallationSlice';
import { convertTimezoneDisplayDate } from '../../helpers/date';
import { translate, translateWithIntegrationName } from '../../helpers/translate';
import { organisationSelectors } from '../../redux/slices/OrganisationSlice';

export interface IDisconnectAppModalProps {
    isLoading: boolean;
    showModal: boolean;
    onSecondaryClicked: () => void;
    onPrimaryClicked: (cancelSubscription?: boolean) => void;
}

const DisconnectAppModal: FC<IDisconnectAppModalProps> = (props) => {
    const { isLoading, showModal, onSecondaryClicked, onPrimaryClicked } = props;

    const installation = useSelector(installationSelectors.installation);
    const organisation = useSelector(organisationSelectors.organisation);

    const integrationLang: any = lang[installation.integrationType];

    const [
        title,
        message,
        radioListLabel,
        subscriptionDisclaimerCancel,
        subscriptionDisclaimerKeep,
        validationMessage,
        cancelButtonText,
        confirmButtonText,
        confirmButtonAlternativeText,
    ] = [
        lang?.MODALS?.DISCONNECT_APP?.MODAL_HEADER,
        lang?.MODALS?.DISCONNECT_APP?.MODAL_MESSAGE(
            integrationLang?.DISPLAY_NAME ?? '',
            integrationLang?.INTEGRATION_NAME,
            integrationLang?.HAS_PARTNER_STORE
        ),
        translateWithIntegrationName(lang?.MODALS?.DISCONNECT_APP?.RADIO_LIST_LABEL, integrationLang?.DISPLAY_NAME),
        translate(lang?.MODALS?.DISCONNECT_APP?.SUBSCRIPTION_DISCLAIMER_CANCEL, {
            nextBillingDate: convertTimezoneDisplayDate(installation.billing?.nextBillingDate!, organisation.timezone),
        }),
        translateWithIntegrationName(
            lang?.MODALS?.DISCONNECT_APP?.SUBSCRIPTION_DISCLAIMER_KEEP,
            integrationLang?.DISPLAY_NAME
        ),
        translateWithIntegrationName(lang?.MODALS?.DISCONNECT_APP?.VALIDATION_MESSAGE, integrationLang?.DISPLAY_NAME),
        lang?.MODALS?.DISCONNECT_APP?.CLOSE_BUTTON,
        lang?.MODALS?.DISCONNECT_APP?.CONFIRM_BUTTON,
        lang?.MODALS.DISCONNECT_APP?.CONFIRM_BUTTON_ALTERNATIVE,
    ];

    // tracks whether the yes or no radio has been selected
    const [confirmRadioState, setConfirmRadioState] = useState(false);

    // a check to see if a user has pressed any radio
    const [isInitialState, setInitialState] = useState(true);

    // a check to see if the warning message should be displayed
    const [isInvalid, setInvalidState] = useState(false);

    const resetModal = () => {
        setInitialState(true);
        setConfirmRadioState(false);
        setInvalidState(false);
    };

    useEffect(() => {
        // reset the modal once it is hidden to be clean every time it is opened
        if (showModal === false) {
            resetModal();
        }
    }, [showModal]);

    const removeInitialState = () => {
        setInitialState(false);

        // remove warning message if the user is in a warning state
        setInvalidState(false);
    };

    const onSelectionChange = (value: boolean) => {
        if (isInitialState) {
            removeInitialState();
        }
        setConfirmRadioState(value);
    };

    // reset state of our radio buttons if the user comes back to the modal after closing it
    const onButtonWrapper = (func: () => void) => {
        // if we are in the initial state, the user needs to select an option
        if (isInitialState) {
            setInvalidState(true);
            return;
        }
        return func();
    };

    return (
        <XUIModal
            isOpen={showModal}
            onClose={onSecondaryClicked}
            closeButtonLabel={lang.MODALS.DISCONNECT_APP.CLOSE_BUTTON}
        >
            <XUIModalHeader>{title}</XUIModalHeader>
            {message}
            <XUIModalBody>
                <XUIRadioGroup label={radioListLabel} isInvalid={isInvalid}>
                    <XUIRadio size='small' isChecked={confirmRadioState} onChange={() => onSelectionChange(true)}>
                        Cancel subscription
                    </XUIRadio>
                    <XUIRadio
                        size='small'
                        isChecked={isInitialState ? false : !confirmRadioState}
                        onChange={() => onSelectionChange(false)}
                    >
                        Keep subscription
                    </XUIRadio>
                </XUIRadioGroup>
                {isInvalid && (
                    <div className='xui-validation xui-validation-layout xui-validation-is-invalid'>
                        <XUIIcon icon={invalidIcon} />
                        {validationMessage}
                    </div>
                )}
            </XUIModalBody>
            {/* Show panel if we have checked the 'no' option and are not in the initial state */}
            {!isInitialState && (
                <XUIModalBody>
                    <XLabsSupportingInfoPanel
                        icon={infoIcon}
                        description={confirmRadioState ? subscriptionDisclaimerCancel : subscriptionDisclaimerKeep}
                    />
                </XUIModalBody>
            )}
            <XUIModalFooter className='xui-actions xui-actions-layout xui-padding-large'>
                <XUIButton
                    onClick={onSecondaryClicked}
                    qaHook='subscription-disconnect-modal-cancel'
                    variant='standard'
                >
                    {cancelButtonText}
                </XUIButton>
                <XUIButton
                    className='xui-actions--primary'
                    onClick={() => onButtonWrapper(() => onPrimaryClicked(confirmRadioState))}
                    qaHook='subscription-disconnect-modal-confirm'
                    variant='negative'
                    isLoading={isLoading}
                    loadingAriaLabel='Loading'
                >
                    {confirmRadioState ? confirmButtonAlternativeText : confirmButtonText}
                </XUIButton>
            </XUIModalFooter>
        </XUIModal>
    );
};

export default DisconnectAppModal;
